// =============================================================================
// UNSUBSCRIBE PAGE COMPONENT
// =============================================================================

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEO';
import { Button } from '@/components/Button';
import { useToastNotifications } from '@/hooks/useToastNotifications';
import './UnsubscribePage.scss';

interface UnsubscribeState {
  status: 'loading' | 'success' | 'error' | 'confirming' | 'not-found';
  message: string;
  email?: string;
}

function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToastNotifications();
  const [state, setState] = useState<UnsubscribeState>({
    status: 'loading',
    message: 'Processing your request...'
  });

  const email = searchParams.get('email');

  useEffect(() => {
    // Always start with the form state, pre-fill email if provided
    if (email) {
      const decodedEmail = decodeURIComponent(email);
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(decodedEmail)) {
        setState({
          status: 'confirming',
          message: 'Please confirm your unsubscribe request.',
          email: decodedEmail
        });
      } else {
        setState({
          status: 'confirming',
          message: 'Please enter a valid email address to unsubscribe.',
          email: ''
        });
      }
    } else {
      setState({
        status: 'confirming',
        message: 'Please enter your email address to unsubscribe.',
        email: ''
      });
    }
  }, [email]);

  const handleUnsubscribe = async () => {
    if (!state.email) return;

    setState(prev => ({
      ...prev,
      status: 'loading',
      message: 'Processing your unsubscribe request...'
    }));

    try {
      const response = await fetch(`${import.meta.env.VITE_FIREBASE_FUNCTIONS}/unsubscribeHttp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: state.email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setState(prev => ({
          ...prev,
          status: 'success',
          message: 'You have been successfully unsubscribed from our newsletter.'
        }));
        showSuccess('Successfully unsubscribed from newsletter');
      } else {
        throw new Error(data.error || 'Failed to unsubscribe');
      }
    } catch (error) {
      // Unsubscribe error
      setState(prev => ({
        ...prev,
        status: 'error',
        message: 'Sorry, there was an error processing your unsubscribe request. Please try again or contact us directly.'
      }));
      showError('Error unsubscribing from newsletter');
    }
  };

  const handleResubscribe = () => {
    navigate('/contact?subject=newsletter-resubscribe');
  };

  const renderContent = () => {
    switch (state.status) {
      case 'loading':
        return (
          <div className="unsubscribe-content loading">
            <div className="spinner"></div>
            <p>{state.message}</p>
          </div>
        );

      case 'confirming':
        return (
          <div className="unsubscribe-content confirming">
            <div className="icon">✉️</div>
            <h2>Unsubscribe from Newsletter</h2>
            <p>
              Enter your email address below to unsubscribe from the Awani newsletter.
            </p>
            <p>
              You'll no longer receive our weekly tech insights and business strategy content.
            </p>
            
            <form onSubmit={(e) => { e.preventDefault(); handleUnsubscribe(); }} className="unsubscribe-form">
              <div className="form-group">
                <input
                  type="email"
                  value={state.email || ''}
                  onChange={(e) => setState(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email address"
                  className="email-input"
                  required
                />
              </div>
              <div className="button-group">
                <Button 
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!state.email || !state.email.includes('@')}
                >
                  Unsubscribe
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  variant="secondary"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        );

      case 'success':
        return (
          <div className="unsubscribe-content success">
            <div className="icon">✅</div>
            <h2>Unsubscribed Successfully</h2>
            <p>
              <strong>{state.email}</strong> has been removed from our newsletter mailing list.
            </p>
            <p>
              You will no longer receive weekly tech insights and business strategy emails from Awani.
            </p>
            <div className="button-group">
              <Button 
                onClick={handleResubscribe}
                variant="secondary"
                size="lg"
              >
                Change Your Mind?
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="primary"
                size="lg"
              >
                Back to Home
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="unsubscribe-content error">
            <div className="icon">❌</div>
            <h2>Error Processing Request</h2>
            <p>{state.message}</p>
            <div className="button-group">
              <Button 
                onClick={() => navigate('/contact')}
                variant="primary"
                size="lg"
              >
                Contact Support
              </Button>
              <Button 
                onClick={() => navigate('/')}
                variant="secondary"
                size="lg"
              >
                Back to Home
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEOHead
        title="Unsubscribe from Newsletter | Awani"
        description="Unsubscribe from the Awani newsletter. Stop receiving weekly tech insights and business strategy emails."
        canonicalUrl="/unsubscribe"
      />
      
      <div className="unsubscribe-page">
        <div className="container">
          <div className="unsubscribe-card">
            <div className="header">
              <h1>Newsletter Unsubscribe</h1>
              <p>Awani</p>
            </div>
            
            {renderContent()}
            
            <div className="footer">
              <p>
                Questions about your subscription? 
                <a href="/contact">Contact us</a> for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export { UnsubscribePage };
